import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Store, User } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthProvider';
import { useActiveChild } from '@/contexts/ActiveChildContext';
import React, { useState, useEffect } from 'react';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { usePlanLabel } from '@/hooks/usePlanLabel';

interface Props {
    onComplete: () => void;
    targetUserIds?: string[]; // Array of user IDs (for multi-child support)
    targetUserId?: string; // Legacy single user support
}

interface SubscriptionInfo {
    userId: string;
    userName: string;
    planName: string;
    amount: number;
}

export const PaymentStep: React.FC<Props> = ({ onComplete, targetUserIds, targetUserId }) => {
    const planLabel = usePlanLabel();
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const { activeChildId } = useActiveChild();
    const { success } = useToast();

    // Determine effective user IDs
    const effectiveUserIds = targetUserIds && targetUserIds.length > 0
        ? targetUserIds
        : targetUserId
            ? [targetUserId]
            : activeChildId
                ? [activeChildId]
                : currentUser?.uid
                    ? [currentUser.uid]
                    : [];

    const [subscriptions, setSubscriptions] = useState<SubscriptionInfo[]>([]);
    const [loadingData, setLoadingData] = useState(true);
    const [processing, setProcessing] = useState(false);

    // Fetch subscription data directly from Firestore for each user
    useEffect(() => {
        const fetchSubscriptionData = async () => {
            if (effectiveUserIds.length === 0) {
                setLoadingData(false);
                return;
            }

            setLoadingData(true);
            console.log('[PaymentStep] Fetching data for users:', effectiveUserIds);

            try {
                const { collection, query, where, getDocs } = await import('firebase/firestore');

                const subInfos: SubscriptionInfo[] = await Promise.all(
                    effectiveUserIds.map(async (userId) => {
                        // Fetch user name
                        const userDoc = await getDoc(doc(db, 'users', userId));
                        const userData = userDoc.exists() ? userDoc.data() : null;
                        const userName = userData?.displayName || 'Unknown User';

                        let planName = 'Membership';
                        let amount = 0;

                        try {
                            // Simplified query - just userId match, filter status client-side
                            // This avoids composite index requirement
                            const subQuery = query(
                                collection(db, 'userSubscriptions'),
                                where('userId', '==', userId)
                            );
                            const subSnapshot = await getDocs(subQuery);

                            // Filter for active subscriptions and sort by createdAt client-side
                            const activeSubs = subSnapshot.docs
                                .map(d => ({ id: d.id, ...d.data() } as Record<string, any>))
                                .filter(sub => sub.status === 'active')
                                .sort((a, b) => {
                                    const aDate = a.createdAt?.toDate?.() || new Date(0);
                                    const bDate = b.createdAt?.toDate?.() || new Date(0);
                                    return bDate.getTime() - aDate.getTime();
                                });

                            if (activeSubs.length > 0) {
                                const subData = activeSubs[0];
                                planName = planLabel(subData) || 'Membership';

                                // First try amountPaid (for subscriptions where payment was already recorded)
                                amount = subData.amountPaid || 0;

                                // If amountPaid is 0 or not set, look up the plan price
                                if (amount === 0 && subData.planId) {
                                    try {
                                        const planDoc = await getDoc(doc(db, 'subscriptionPlans', subData.planId));
                                        if (planDoc.exists()) {
                                            const planData = planDoc.data();
                                            amount = planData.price || 0;
                                            console.log('[PaymentStep] Fetched plan price for', planName, ':', amount);
                                        }
                                    } catch (planErr) {
                                        console.error('[PaymentStep] Error fetching plan price:', planErr);
                                    }
                                }
                            }
                        } catch (queryErr) {
                            console.error('[PaymentStep] Error querying subscription for', userId, queryErr);
                        }

                        console.log('[PaymentStep] User', userId, 'subscription:', { planName, amount });

                        return {
                            userId,
                            userName,
                            planName,
                            amount
                        };
                    })
                );
                setSubscriptions(subInfos);
            } catch (err) {
                console.error('Error fetching subscription data:', err);
                // Set empty subscriptions to unblock the UI
                setSubscriptions(effectiveUserIds.map(userId => ({
                    userId,
                    userName: 'User',
                    planName: 'Membership',
                    amount: 0
                })));
            } finally {
                setLoadingData(false);
            }
        };

        fetchSubscriptionData();
    }, [effectiveUserIds.join(',')]);

    const totalAmount = subscriptions.reduce((sum, s) => sum + s.amount, 0);

    const handlePayAtDesk = async () => {
        setProcessing(true);
        // Simulate processing delay
        await new Promise(resolve => setTimeout(resolve, 800));

        success("Registration finalized! Please see the front desk to complete payment.");
        setProcessing(false);
        onComplete();
    };

    if (effectiveUserIds.length === 0) return null;

    // Show loading while fetching data
    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground text-sm">{t('onboarding.payment.loading_details')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">{t('onboarding.payment.title')}</h2>
                <p className="text-muted-foreground">{t('onboarding.payment.subtitle')}</p>
            </div>

            <Card className="max-w-lg mx-auto border-2 border-primary/10 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl">{t('onboarding.payment.summary')}</CardTitle>
                    <CardDescription>
                        {subscriptions.length > 1
                            ? t('onboarding.payment.amount_due_multi', { count: subscriptions.length })
                            : t('onboarding.payment.amount_due_single', { plan: planLabel(subscriptions[0]) || t('subscriptions.membership') })
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Itemized list for multiple users */}
                    {subscriptions.length > 1 && (
                        <div className="space-y-3 pb-4 border-b">
                            {subscriptions.map((sub) => (
                                <div key={sub.userId} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                            <User className="h-4 w-4 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">{sub.userName}</p>
                                            <p className="text-xs text-muted-foreground">{planLabel(sub)}</p>
                                        </div>
                                    </div>
                                    <span className="font-medium">{formatCurrency(sub.amount)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Total */}
                    <div className="flex justify-between items-center py-2">
                        <span className="font-medium text-lg">{t('onboarding.payment.total_due_today')}</span>
                        <span className="text-3xl font-bold text-primary">
                            {formatCurrency(totalAmount)}
                        </span>
                    </div>

                    <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                        <div className="flex items-start gap-3">
                            <Store className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                            <div className="space-y-1">
                                <p className="font-medium text-sm">{t('onboarding.payment.pay_at_desk')}</p>
                                <p className="text-xs text-muted-foreground">
                                    {t('onboarding.payment.pay_at_desk_hint')}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
                <CardFooter>
                    <Button
                        className="w-full"
                        size="lg"
                        onClick={handlePayAtDesk}
                        disabled={processing}
                    >
                        {processing ? t('onboarding.payment.finalizing') : t('onboarding.payment.confirm_finish')}
                    </Button>
                </CardFooter>
            </Card>

            <div className="text-center">
                <Button variant="link" className="text-xs text-muted-foreground" onClick={onComplete}>
                    {t('onboarding.payment.already_paid')}
                </Button>
            </div>
        </div>
    );
};

